/*
 * This code is protected by intellectual property rights.
 * Dr. Ing. h.c. F. Porsche AG owns exclusive rights of use.
 * © 2017-2026, Dr. Ing. h.c. F. Porsche AG.
 */

import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild, inject, ChangeDetectionStrategy } from '@angular/core'
import { UntypedFormGroup, Validators } from '@angular/forms'
import {
  DISABLED_MATCHER,
  DynamicFormComponent,
  DynamicFormControlModel,
  DynamicFormLayout,
  DynamicFormModel,
  DynamicFormRelationService,
  DynamicFormService,
  DynamicInputModel,
  HIDDEN_MATCHER,
} from '@myprofile-ui/myprofile-dynamic-forms'
import { PidxFormValidationTrackingDirective, WINDOW } from '@myprofile-ui/pidx-utils'
import { Actions, Store, ofActionCompleted } from '@ngxs/store'
import cloneDeep from 'lodash-es/cloneDeep'
import { Observable, Subject, forkJoin, mergeMap, of } from 'rxjs'
import { filter, takeUntil, distinctUntilChanged, switchMap, take } from 'rxjs/operators'
import { MyProfileAddressWidgetMode } from '../../enums/myprofile-address-widget-mode'
import { MyProfileAddressDeleteHelperService } from '../../services/helpers/myprofile-address-delete-helper.service'
import { MyProfileAddressHelperService } from '../../services/helpers/myprofile-address-helper.service'
import {
  AddSchemaForCountry,
  CreateAddress,
  SetEditorLoadingStatus,
  SetEditorSchema,
  ShowBlankEditor,
  UpdateAddress,
  UpdateTmpAddress,
} from '../../store/editor.actions'
import { SetAddressBeingEdited } from '../../store/list.actions'
import { HttpHeaders } from '@angular/common/http'
import { v4 as uuid } from 'uuid'
import { PidxLoaderModeEnum } from '@myprofile-ui/pidx-ui-components'

@Component({
  selector: 'myprofile-address-editform',
  templateUrl: './myprofile-address-editform.component.html',
  host: { class: 'profile:block profile:relative', '[attr.tabindex]': '-1' },
  standalone: false,
  // Angular 22 changed the implicit default to OnPush (see angular/angular#67687). This component was never migrated
  // to OnPush semantics, so Eager restores the pre-v22 behaviour it relies on.
  // eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class MyProfileAddressEditFormComponent implements OnInit, OnDestroy {
  private window = inject<Window>(WINDOW)
  private formService = inject(DynamicFormService)
  private formRelationService = inject(DynamicFormRelationService)
  private store = inject(Store)
  private actions$ = inject(Actions)
  private myProfileAddressDeleteHelperService = inject(MyProfileAddressDeleteHelperService)
  private elementRef = inject<ElementRef<HTMLElement>>(ElementRef)

  @Input() instanceId!: string
  @Input() token!: string

  @Input() saveButtonLabel!: string
  @Input() cancelButtonLabel!: string
  @Input() deleteButtonLabel?: string
  @Input() editorLoadingLabel!: string
  @Input() saveAndContinueButtonLabel!: string
  @Input() mode: MyProfileAddressWidgetMode = MyProfileAddressWidgetMode.CHECKOUT

  @ViewChild(DynamicFormComponent, { static: false }) formComponent!: DynamicFormComponent
  @ViewChild(PidxFormValidationTrackingDirective, { static: false }) htmlForm: any

  public isBlankCheckoutModeEditor$!: Observable<any>
  public schema$!: Observable<any>
  public layout$!: Observable<DynamicFormLayout>
  public editorIsLoading$!: Observable<boolean>
  public errorMessage$!: Observable<string | null>

  public formModel!: DynamicFormModel
  public formGroup!: UntypedFormGroup
  public addressIdBeingEdited: string | undefined

  private unsubscribe$ = new Subject<void>()
  private recipientCodeUnsubscribe$ = new Subject<void>()
  private usageRelationUnsubscribe$ = new Subject<void>()
  private currentlySelectedCountry!: string
  private _headers!: HttpHeaders

  public ngOnInit() {
    if (this.mode === MyProfileAddressWidgetMode.CHECKOUT || this.mode === MyProfileAddressWidgetMode.MYLANTA) {
      this._headers = new HttpHeaders({
        Authorization: `Bearer ${this.token}`,
        'X-TRACE-ID': `MYPROFILE-ADDRESS-WIDGET-${uuid()}`,
      })
    }
    this.setupStateSubscriber()
    this.setupActionListeners()

    this.formGroup = new UntypedFormGroup({})

    this.store
      .select(({ addressWidget }) => addressWidget.widgetInstances[this.instanceId].editor.country.toUpperCase())
      .pipe(
        distinctUntilChanged(),
        switchMap((country) =>
          this.store
            .select(({ addressWidget }) => addressWidget.schemas[country])
            .pipe(
              filter((schema) => !!schema),
              take(1),
            ),
        ),
        takeUntil(this.unsubscribe$),
      )
      .subscribe((schema) => {
        this.renderForm(this.extendSchemaWithExistingFormData(schema))
      })
  }

  public submitForm(formGroup: UntypedFormGroup) {
    // The company block is only mandatory for business ("work") addresses. Re-assert the usage-driven
    // disabled/hidden state right before validating, so a private ("home") address is never blocked by the
    // company fields — independent of any earlier container/relation initialisation race (which differs
    // between edit and create, and under Angular Elements, where edit re-renders the form twice).
    this.enforceUsageDrivenRelations()

    MyProfileAddressHelperService.triggerFormValidationErrors(formGroup)

    if (formGroup.invalid) {
      return
    }

    const addressData = MyProfileAddressHelperService.prepareAddressPayload(formGroup, this.addressIdBeingEdited as string)

    // This is necessary due to the BFF cant handle '-'-chars in the payload for JP (country-specific logic).
    // We do NOT correct the frontend-validation here since people there are used to enter the dash
    // FYI: For PL is is mandatory to send the string, for SE to send the space in the postalCode!
    const tmpCountry = this.store.selectSnapshot(({ addressWidget }) =>
      addressWidget.widgetInstances[this.instanceId].editor.tmpAddress?.country?.toUpperCase(),
    )
    if (tmpCountry?.toLowerCase() === 'jp' && addressData.postalCode) {
      addressData.postalCode = MyProfileAddressHelperService.getStringWithoutDashes(addressData.postalCode)
    }

    // We're updating an existing address
    if (!addressData.addressId) {
      this.store.dispatch(new CreateAddress(addressData, this.instanceId, this._headers)).subscribe()
      // New-address flow: return focus to "Add address". Inline list edit uses address list focus restore instead.
      this.setFocusOnElementId('addAddressBtn')
    } else {
      this.store.dispatch(new UpdateAddress(addressData, this.instanceId, this._headers)).subscribe()
    }
  }

  public deleteAddress() {
    const tmpAddress = this.store.selectSnapshot(({ addressWidget }) => addressWidget.widgetInstances[this.instanceId].editor.tmpAddress)
    if (tmpAddress) {
      this.myProfileAddressDeleteHelperService.prepareDeleteItem(tmpAddress, this.instanceId)
    }
  }

  public cancel() {
    const wasEditingExistingAddress = !!this.addressIdBeingEdited
    this.store.dispatch(new ShowBlankEditor(false, this.instanceId)).subscribe()
    // Add-address / blank-editor flow: focus "Add address". List inline edit restores focus on the Edit control.
    if (!wasEditingExistingAddress) {
      this.setFocusOnElementId('addAddressBtn')
    }
  }

  public ngOnDestroy() {
    this.unsubscribe$.next()
    this.unsubscribe$.complete()
    this.recipientCodeUnsubscribe$.next()
    this.recipientCodeUnsubscribe$.complete()
    this.usageRelationUnsubscribe$.next()
    this.usageRelationUnsubscribe$.complete()
  }

  private setupStateSubscriber() {
    this.schema$ = this.store.select((state) => {
      return state.addressWidget.schemas[state.addressWidget.widgetInstances[this.instanceId].editor.country.toUpperCase()]
    })

    this.layout$ = this.store.select(
      ({ addressWidget }) => addressWidget.layouts[addressWidget.widgetInstances[this.instanceId].editor.country.toUpperCase()],
    )

    this.addressIdBeingEdited = this.store.selectSnapshot(
      ({ addressWidget }) => addressWidget.widgetInstances[this.instanceId].addressIdBeingEdited,
    )

    this.isBlankCheckoutModeEditor$ = this.store.select(
      ({ addressWidget }) =>
        addressWidget.widgetInstances[this.instanceId].mode === MyProfileAddressWidgetMode.CHECKOUT && !addressWidget.addresses.length,
    )

    this.editorIsLoading$ = this.store.select(({ addressWidget }) => addressWidget.widgetInstances[this.instanceId].editor.loading)

    this.errorMessage$ = this.store.select(({ addressWidget }) => addressWidget.widgetInstances[this.instanceId].editor.errorMessage)
  }

  // We need to listen for certain actions to be successful or completed to follow up on them
  private setupActionListeners() {
    this.actions$
      .pipe(
        ofActionCompleted(SetAddressBeingEdited),
        filter(({ action }) => action.instanceId === this.instanceId),
        takeUntil(this.unsubscribe$),
      )
      .subscribe(() => {
        this.addressIdBeingEdited = this.store.selectSnapshot(
          ({ addressWidget }) => addressWidget.widgetInstances[this.instanceId].addressIdBeingEdited,
        )

        this.renderForm(
          this.extendSchemaWithExistingFormData(
            this.store.selectSnapshot(
              ({ addressWidget }) => addressWidget.schemas[addressWidget.widgetInstances[this.instanceId].editor.country.toUpperCase()],
            ),
          ),
        )
      })
  }

  private renderForm(schema: any) {
    this.formModel = this.formService.fromJSON(JSON.stringify(schema))
    this.formGroup = this.formService.createFormGroup(this.formModel)

    this.setupCountrySelectorChangeListener()
    this.setupRecipientCodeListener()
    this.setupUsageRelationEnforcement()
  }

  // Group fields such as the company block are only mandatory for business ("work") addresses, which the schema models
  // with enable/disable (and visible/hidden) relations gated on the usage radio. Under Angular Elements (shared web
  // components) the dynamic-form container initialisation can race, so the relation engine's initial evaluation is
  // missed and the group stays enabled on first render — a private ("home") address then cannot be saved until the user
  // toggles usage to "work" and back. Rather than depend on that container timing, enforce the usage-driven disabled and
  // hidden state deterministically here, straight on the controls/models, on first render and on every usage change. We
  // reuse the relation service's own matching logic so the evaluation stays identical to the declarative schema.
  private setupUsageRelationEnforcement() {
    this.usageRelationUnsubscribe$.next()

    const usageControl = this.formGroup.get('usage')

    if (!usageControl) {
      return
    }

    this.enforceUsageDrivenRelations()

    usageControl.valueChanges
      .pipe(takeUntil(this.usageRelationUnsubscribe$), takeUntil(this.unsubscribe$))
      .subscribe(() => this.enforceUsageDrivenRelations())
  }

  private enforceUsageDrivenRelations() {
    this.formModel.forEach((model) => this.enforceModelRelations(model))
    this.formComponent?.detectChanges()
  }

  private enforceModelRelations(model: DynamicFormControlModel) {
    if (!model.relations?.length) {
      return
    }

    const control = this.formGroup.get(model.id)

    if (!control) {
      return
    }

    const relatedControls = this.formRelationService.getRelatedFormControls(model, this.formGroup)

    const disableRelation = this.formRelationService.findRelationByMatcher(model.relations, DISABLED_MATCHER)

    if (disableRelation) {
      const shouldDisable = this.formRelationService.matchesCondition(disableRelation, relatedControls, DISABLED_MATCHER)

      model.disabled = shouldDisable

      if (shouldDisable && !control.disabled) {
        control.disable({ emitEvent: false })
      } else if (!shouldDisable && control.disabled) {
        control.enable({ emitEvent: false })
      }
    }

    const hideRelation = this.formRelationService.findRelationByMatcher(model.relations, HIDDEN_MATCHER)

    if (hideRelation) {
      model.hidden = this.formRelationService.matchesCondition(hideRelation, relatedControls, HIDDEN_MATCHER)
    }
  }

  private setupCountrySelectorChangeListener() {
    const countrySelector = this.formService.findById('country', this.formModel) as DynamicInputModel

    ;(countrySelector.valueChanges as Observable<string>)
      .pipe(
        filter((value) => !!value && this.currentlySelectedCountry !== value),
        takeUntil(this.unsubscribe$),
        mergeMap((value: string) => {
          if (this.currentlySelectedCountry) {
            this.store.dispatch(new SetEditorLoadingStatus(true)).subscribe()
          }

          // PROFILE-10794 +++ Since the country abbreviation
          // XA (Abu Dhabi) and XD (Dubai) means the country
          // AE (United Arab Emirates), this workaround was
          // introduced here to select AE as the default
          // address country.
          if (value === 'XA' || value === 'XD') {
            value = 'AE'
          }

          this.currentlySelectedCountry = value

          // keep the address usage that was set before when updating the country
          const newUsage = this.formGroup.get('usage')?.value
          const tmpAddress = this.store.selectSnapshot(
            ({ addressWidget }) => addressWidget.widgetInstances[this.instanceId].editor.tmpAddress,
          )

          this.store
            .dispatch(
              new UpdateTmpAddress(
                {
                  ...tmpAddress,
                  country: value,
                  usage: newUsage,
                },
                this.instanceId,
              ),
            )
            .subscribe()

          return forkJoin([this.store.dispatch(new AddSchemaForCountry(value)), of(value)])
        }),
      )
      .subscribe(([_, country]) => {
        this.store.dispatch(new SetEditorLoadingStatus(false)).subscribe()
        this.store.dispatch(new SetEditorSchema(country, this.instanceId)).subscribe()

        // addListener is a public method from the directive
        this.htmlForm.addListener()

        this.window.setTimeout(() => {
          this.formComponent.markForCheck()
          this.formComponent.detectChanges()

          // dynamic-form-control destroys/recreates <profile-pds-p-select> when the
          // model reference changes, which drops focus to <body>. Re-focus the country
          // select so keyboard users keep their place after a country change (WCAG 2.4.3).
          this.elementRef.nativeElement.querySelector<HTMLElement>('#country')?.focus()
        }, 0)
      })
  }

  // recipientCode depends on the selected recipientCodeType: it is only shown once a type
  // is selected and is required while visible. Its label stays static (backend schema),
  // while its description reflects the selected option's label.
  private setupRecipientCodeListener() {
    const typeModel = this.formService.findModelById('recipientCodeType', this.formModel) as unknown as
      (DynamicInputModel & { options: { value: unknown; label: string | null }[] }) | null
    const codeModel = this.formService.findModelById<DynamicInputModel>('recipientCode', this.formModel)

    if (!typeModel || !codeModel) {
      return
    }

    const typeControl = this.formService.findControlByModel(typeModel, this.formGroup)
    const codeControl = this.formService.findControlByModel(codeModel, this.formGroup)

    if (!typeControl || !codeControl) {
      return
    }

    const applyRecipientCodeState = (typeValue: string) => {
      const hasType = !!typeValue

      const selectedOption = hasType ? typeModel.options.find((option) => String(option.value) === typeValue) : undefined

      codeModel.description = hasType ? (selectedOption?.label ?? '') : ''
      codeModel.hidden = !hasType
      codeModel.required = hasType

      if (hasType) {
        codeControl.setValidators(Validators.required)
      } else {
        codeControl.clearValidators()

        if (codeControl.value) {
          codeControl.setValue('', { emitEvent: false })
        }
      }

      codeControl.updateValueAndValidity({ emitEvent: false })

      this.formComponent?.markForCheck()
      this.formComponent?.detectChanges()
    }

    applyRecipientCodeState(typeControl.value)

    this.recipientCodeUnsubscribe$.next()

    typeControl.valueChanges
      .pipe(takeUntil(this.recipientCodeUnsubscribe$), takeUntil(this.unsubscribe$))
      .subscribe((value: string) => applyRecipientCodeState(value))
  }

  // In order to fill the form inputs with data, we need to extend the schema with the address data
  private extendSchemaWithExistingFormData(schema: any): any {
    const copiedSchema = cloneDeep(schema)
    const addressObjBeingEdited = this.store.selectSnapshot(
      ({ addressWidget }) => addressWidget.widgetInstances[this.instanceId].editor.tmpAddress,
    )

    const numberOfAddresses = this.store.selectSnapshot(({ addressWidget }) => addressWidget.addresses)

    if (addressObjBeingEdited) {
      copiedSchema.forEach((obj: any) => {
        if (obj.type === 'GROUP') {
          obj.group.forEach((item: any) => {
            item.value = addressObjBeingEdited[item.id] == null ? '' : addressObjBeingEdited[item.id]
          })
        } else {
          obj.value = addressObjBeingEdited[obj.id] == null ? '' : addressObjBeingEdited[obj.id]
        }
      })

      const isStandardEntry = copiedSchema.find(({ id }: { id: string }) => id === 'isStandard')
      if (addressObjBeingEdited.isStandard === true) {
        isStandardEntry.disabled = true
      }
      if (!numberOfAddresses || numberOfAddresses?.length === 0) {
        isStandardEntry.value = true
        isStandardEntry.disabled = true
      }
    }
    return copiedSchema
  }

  public addressAutocompleteResultSelected($event: any) {
    Object.entries($event).forEach(([key, value]) => {
      const model = this.formService.findModelById(key, this.formModel)

      if (model) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        model.value$.next(value)
      }
    })
  }

  private setFocusOnElementId(id: string) {
    setTimeout(() => {
      const forFocus: HTMLElement | null = document.getElementById(id)
      if (forFocus) {
        forFocus.focus()
      }
    }, 500)
  }

  protected readonly PidxLoaderModeEnum = PidxLoaderModeEnum
}
